/**
 * AdminAIPageCreator — two-phase modal for AI-powered CMS page generation.
 *
 * Phase 1: Admin fills in page context (purpose, audience, key messages, tone, sections).
 * Phase 2: Preview the generated HTML; accept to pre-fill the Monaco editor or regenerate.
 *
 * Calls generateWithAI() directly from the browser — same pattern as Brevo email.
 */

import { useState } from 'react';
import {
  Sparkles,
  X,
  RefreshCw,
  CheckCircle2,
  Loader2,
  ChevronRight,
  ArrowLeft,
  AlertCircle,
} from 'lucide-react';
import { generateWithAI } from '@/lib/ai';
import type { AIConfig } from '@/lib/cms';

type Tone = 'professional' | 'authoritative' | 'approachable';

interface SectionOption {
  id: string;
  label: string;
  description: string;
}

const SECTION_OPTIONS: SectionOption[] = [
  { id: 'hero', label: 'Hero Section', description: 'Full-width hero with headline, subtext, and optional stats' },
  { id: 'features', label: 'Feature Cards', description: 'Icon cards highlighting key capabilities or benefits' },
  { id: 'how-it-works', label: 'How It Works', description: 'Numbered steps explaining the process' },
  { id: 'social-proof', label: 'Social Proof / Stats', description: 'Credibility stats or short testimonial quotes' },
  { id: 'cta', label: 'CTA Section', description: 'Dark closing section with call-to-action buttons' },
];

const TONE_OPTIONS: { value: Tone; label: string; description: string }[] = [
  { value: 'professional', label: 'Professional', description: 'Clear, polished, business-oriented' },
  { value: 'authoritative', label: 'Authoritative', description: 'Confident expert positioning' },
  { value: 'approachable', label: 'Approachable', description: 'Warm, helpful, plain-language' },
];

export interface AdminAIPageCreatorProps {
  aiConfig: AIConfig;
  onAccept: (html: string) => void;
  onClose: () => void;
}

type Phase = 'input' | 'generating' | 'preview';

export function AdminAIPageCreator({ aiConfig, onAccept, onClose }: AdminAIPageCreatorProps): JSX.Element {
  // Phase 1 inputs
  const [purpose, setPurpose] = useState('');
  const [audience, setAudience] = useState('');
  const [keyMessages, setKeyMessages] = useState('');
  const [tone, setTone] = useState<Tone>('professional');
  const [selectedSections, setSelectedSections] = useState<Set<string>>(
    new Set(['hero', 'features', 'cta'])
  );

  // Phase state
  const [phase, setPhase] = useState<Phase>('input');
  const [generatedHTML, setGeneratedHTML] = useState('');
  const [error, setError] = useState('');

  const toggleSection = (id: string) => {
    setSelectedSections((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        // Always keep at least one section
        if (next.size > 1) next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const buildUserPrompt = (): string => {
    const sections = SECTION_OPTIONS.filter((s) => selectedSections.has(s.id))
      .map((s) => `- ${s.label}`)
      .join('\n');

    const toneNote =
      tone === 'professional'
        ? 'Clear, polished, business-oriented. Avoid jargon but stay precise.'
        : tone === 'authoritative'
        ? 'Confident and expert. Cite specific patient-support and community-impact concepts. Position GSGO as a trusted authority.'
        : 'Warm, plain-language, approachable. Use "you" language and avoid formal stiffness.';

    return `Generate a complete CMS page HTML for the following:

**Page purpose / title**: ${purpose.trim()}

**Target audience**: ${audience.trim() || 'Federal contractors and small businesses pursuing government contracts'}

**Key messages to convey**:
${keyMessages.trim() || '- GSGO supports CKD patients and families through practical programs\n- Community-first, mission-driven approach\n- Trusted education, advocacy, and partner collaboration'}

**Tone**: ${tone} — ${toneNote}

**Sections to include**:
${sections}

Generate the full page HTML following all brand and inline-style standards. Output only raw HTML, starting with the outermost <div class="not-prose"> wrapper.`;
  };

  const handleGenerate = async () => {
    if (!purpose.trim()) {
      setError('Please enter a page purpose or title.');
      return;
    }
    setError('');
    setPhase('generating');

    try {
      const html = await generateWithAI({
        config: aiConfig,
        userPrompt: buildUserPrompt(),
      });
      setGeneratedHTML(html);
      setPhase('preview');
    } catch (err) {
      console.error('[AdminAIPageCreator] Generation failed:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'AI generation failed. Check your API key and try again.'
      );
      setPhase('input');
    }
  };

  const handleRegenerate = () => {
    setPhase('input');
    setGeneratedHTML('');
  };

  const handleAccept = () => {
    onAccept(generatedHTML);
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Generate page with AI"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={phase !== 'generating' ? onClose : undefined}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl rounded-2xl bg-white shadow-2xl flex flex-col max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-2.5">
            <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-violet-100 text-violet-600">
              <Sparkles size={16} />
            </span>
            <div>
              <h2 className="text-base font-bold text-gray-900">Generate with AI</h2>
              <p className="text-xs text-gray-400">
                {phase === 'preview'
                  ? 'Review the generated HTML and accept or regenerate'
                  : 'Describe the page and we\'ll generate on-brand HTML'}
              </p>
            </div>
          </div>
          {phase !== 'generating' && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 min-h-0 overflow-y-auto">
          {/* Phase 1: Input */}
          {phase === 'input' && (
            <div className="px-6 py-5 space-y-5">
              {error && (
                <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
                  <AlertCircle size={15} className="text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              )}

              {/* Page purpose */}
              <div className="space-y-1.5">
                <label htmlFor="ai-purpose" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Page purpose / title <span className="text-red-500">*</span>
                </label>
                <input
                  id="ai-purpose"
                  type="text"
                  value={purpose}
                  onChange={(e) => setPurpose(e.target.value)}
                  placeholder="e.g. Services page explaining our 5 BD acceleration packages"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                  autoFocus
                />
              </div>

              {/* Audience */}
              <div className="space-y-1.5">
                <label htmlFor="ai-audience" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Target audience
                </label>
                <input
                  id="ai-audience"
                  type="text"
                  value={audience}
                  onChange={(e) => setAudience(e.target.value)}
                  placeholder="e.g. Federal contractors $5M–$50M, new to teaming"
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500"
                />
              </div>

              {/* Key messages */}
              <div className="space-y-1.5">
                <label htmlFor="ai-messages" className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Key messages
                </label>
                <textarea
                  id="ai-messages"
                  rows={4}
                  value={keyMessages}
                  onChange={(e) => setKeyMessages(e.target.value)}
                  placeholder={"- We accelerate BD pipeline by 3x\n- Compliance-first approach reduces risk\n- Expert teaming and 8(a) set-aside strategy"}
                  className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 shadow-sm focus:border-violet-500 focus:ring-1 focus:ring-violet-500 resize-none font-mono text-xs"
                />
                <p className="text-xs text-gray-400">One point per line. Leave blank to use GSGO defaults.</p>
              </div>

              {/* Tone */}
              <div className="space-y-2">
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">Tone</span>
                <div className="flex flex-wrap gap-2">
                  {TONE_OPTIONS.map((t) => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => setTone(t.value)}
                      className={`flex flex-col items-start rounded-lg border px-3 py-2 text-left transition-colors ${
                        tone === t.value
                          ? 'border-violet-400 bg-violet-50 text-violet-800'
                          : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      <span className="text-sm font-semibold">{t.label}</span>
                      <span className="text-xs opacity-70 mt-0.5">{t.description}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Sections */}
              <div className="space-y-2">
                <span className="block text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Sections to include
                </span>
                <div className="grid grid-cols-1 gap-2">
                  {SECTION_OPTIONS.map((s) => {
                    const checked = selectedSections.has(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 cursor-pointer transition-colors ${
                          checked
                            ? 'border-violet-300 bg-violet-50'
                            : 'border-gray-200 bg-white hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleSection(s.id)}
                          className="h-4 w-4 rounded border-gray-300 text-violet-600 focus:ring-violet-500"
                        />
                        <div>
                          <span className={`text-sm font-medium ${checked ? 'text-violet-800' : 'text-gray-700'}`}>
                            {s.label}
                          </span>
                          <span className="ml-2 text-xs text-gray-400">{s.description}</span>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              {/* Provider badge */}
              <div className="flex items-center gap-2 text-xs text-gray-400">
                <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2 py-0.5 font-medium text-gray-500">
                  {aiConfig.provider === 'openai' ? 'OpenAI' : 'Anthropic'}
                  {' · '}
                  {aiConfig.model}
                </span>
                <span>Change in Settings → AI Content Generation</span>
              </div>
            </div>
          )}

          {/* Phase: Generating */}
          {phase === 'generating' && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <div className="relative">
                <div className="w-16 h-16 rounded-2xl bg-violet-100 flex items-center justify-center">
                  <Sparkles size={28} className="text-violet-600" />
                </div>
                <Loader2 size={20} className="text-violet-500 animate-spin absolute -top-1 -right-1" />
              </div>
              <div className="text-center">
                <p className="text-base font-semibold text-gray-800">Generating your page…</p>
                <p className="text-sm text-gray-400 mt-1">
                  {aiConfig.provider === 'openai' ? 'Calling OpenAI' : 'Calling Anthropic Claude'} · This takes 10–30 seconds
                </p>
              </div>
            </div>
          )}

          {/* Phase 2: Preview */}
          {phase === 'preview' && (
            <div className="flex flex-col h-full">
              {/* Preview toolbar */}
              <div className="flex items-center gap-2 px-6 py-3 border-b border-gray-100 bg-gray-50 shrink-0">
                <button
                  type="button"
                  onClick={handleRegenerate}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  <ArrowLeft size={14} />
                  Edit inputs
                </button>
                <span className="text-xs text-gray-400 ml-auto">
                  {generatedHTML.length.toLocaleString()} characters
                </span>
              </div>
              {/* Code preview */}
              <div className="flex-1 min-h-0 overflow-y-auto bg-[#1e1e1e]">
                <pre className="p-5 text-xs text-gray-300 font-mono leading-relaxed whitespace-pre-wrap break-all">
                  {generatedHTML}
                </pre>
              </div>
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-100 bg-gray-50 shrink-0">
          {phase === 'input' && (
            <>
              <button
                type="button"
                onClick={onClose}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => { void handleGenerate(); }}
                disabled={!purpose.trim()}
                className={`inline-flex items-center gap-2 rounded-lg px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors ${
                  purpose.trim()
                    ? 'bg-violet-600 hover:bg-violet-700'
                    : 'bg-violet-300 cursor-not-allowed'
                }`}
              >
                <Sparkles size={14} />
                Generate page
                <ChevronRight size={14} />
              </button>
            </>
          )}

          {phase === 'generating' && (
            <div className="flex items-center gap-2 text-sm text-gray-400 mx-auto">
              <Loader2 size={14} className="animate-spin" />
              Generating — please wait…
            </div>
          )}

          {phase === 'preview' && (
            <>
              <button
                type="button"
                onClick={() => { void handleGenerate(); }}
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
              >
                <RefreshCw size={14} />
                Regenerate
              </button>
              <button
                type="button"
                onClick={handleAccept}
                className="inline-flex items-center gap-2 rounded-lg bg-violet-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 transition-colors"
              >
                <CheckCircle2 size={14} />
                Use this content
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
